import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import EquanautSidebar from './components/EquanautSidebar';
import { BotIcon } from './components/Icons';
import { useChat } from './hooks/useChat';
import { useGatewayChat } from './hooks/useGatewayChat';
import { useAgentContext, AgentContextProvider } from './context/AgentContextProvider';
import { agentService } from './services/api/agent';
import { API_CONFIG } from './services/api/config';
import { resolveEquanautSessionKey } from './services/gateway/session-resolver';
import type { UIMessage, Conversation } from './services/api/chat/types';
import type { UserContext } from './services/api/config';
import type { FirstLoginResponse } from './services/api/agent/types';
import type { ConnectionStatus } from './components/StatusBar';
import { primePath } from '@logic';
import type { User, Organization } from '@logic';
import type { StateTransform } from '@src/index';

// Storage keys
const STORAGE_KEY = 'equanaut_conversations';
const FIRST_LOGIN_KEY = 'equanaut_first_login_handled';
const OPEN_TAB_STORAGE_KEY = 'equanaut_open_tab';

type CommandCenterTab = 'threads' | 'fleet' | 'status';

function isCommandCenterTab(value: string | null): value is CommandCenterTab {
  return value === 'threads' || value === 'fleet' || value === 'status';
}

function loadConversations(): Conversation[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    // Ignore parse errors
  }
  return [];
}

function saveConversations(conversations: Conversation[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
  } catch (e) {
    // Ignore storage errors
  }
}

interface StateProps {
  user?: User;
  currentOrganization?: Organization;
}

// Inner component that uses agent context
const EquanautInner: React.FC<StateProps & {
  isOpen: boolean;
  onClose: () => void;
  firstLoginContext?: FirstLoginResponse | null;
}> = ({
  user,
  currentOrganization,
  isOpen,
  onClose,
  firstLoginContext
}) => {
    const [conversations, setConversations] = useState<Conversation[]>(loadConversations);
    const [activeConversationId, setActiveConversationId] = useState<string | undefined>(undefined);
    const [commandCenterMode, setCommandCenterMode] = useState(false);
    const [initialCommandCenterTab, setInitialCommandCenterTab] = useState<CommandCenterTab | undefined>(undefined);

    // Get agent context
    const { isAgentEnabled, setOrganizationId } = useAgentContext();

    // Session key resolution: CC mode uses agent:main:main, normal uses equa-{orgId}
    const sessionKey = useMemo(() => {
      return resolveEquanautSessionKey(
        currentOrganization?.id,
        commandCenterMode ? 'command-center' : 'equanaut',
      );
    }, [commandCenterMode, currentOrganization?.id]);

    // Update organization ID when it changes
    useEffect(() => {
      setOrganizationId(currentOrganization?.id || null);
    }, [currentOrganization?.id, setOrganizationId]);

    // One-shot handoff from /ai/tasks: open Command Center directly to a tab.
    useEffect(() => {
      let requestedTab: string | null = null;
      try {
        requestedTab = sessionStorage.getItem(OPEN_TAB_STORAGE_KEY);
      } catch {
        // Storage can be unavailable in private-browsing modes.
      }

      if (!isCommandCenterTab(requestedTab)) return;

      setCommandCenterMode(true);
      setInitialCommandCenterTab(requestedTab);
      try {
        sessionStorage.removeItem(OPEN_TAB_STORAGE_KEY);
      } catch {
        // Best-effort cleanup only.
      }
    }, []);

    // Build user context from Redux state
    const userContext = useMemo<UserContext | undefined>(() => {
      if (!user) return undefined;
      return {
        userName: user.profile && user.profile.fullName ? user.profile.fullName : undefined,
        userEmail: user.email,
        organizationName: currentOrganization ? currentOrganization.name : undefined,
        organizationId: currentOrganization ? currentOrganization.id : undefined,
        companyType: currentOrganization ? currentOrganization.type : undefined,
      };
    }, [user, currentOrganization]);

    // Two-tier chat: gateway (with built-in agent fallback) > basic
    const regularChat = useChat(userContext);
    const gatewayChat = useGatewayChat({
      organizationId: currentOrganization?.id,
      commandCenterMode,
      sessionKey,
    });

    // Command Center is gateway-first; regular Equanaut keeps the org/direct-agent gate.
    const useGatewayTier = API_CONFIG.GATEWAY.GATEWAY_ENABLED
      && (commandCenterMode || (isAgentEnabled && !!currentOrganization?.id));

    const activeChat = useGatewayTier ? gatewayChat : regularChat;
    const {
      messages,
      isLoading,
      error,
      sendMessage,
      clearMessages,
      setMessages,
    } = activeChat;

    // Gateway-specific state (basic chat has none)
    const executingSteps = useGatewayTier ? gatewayChat.executingSteps : [];
    const pendingAction = useGatewayTier ? gatewayChat.pendingAction : null;
    const confirmAction = useGatewayTier ? gatewayChat.confirmAction : undefined;

    // Connection status: gateway tier uses hook status; basic tier shows "Basic Chat" (Spec 054 Phase 3.4)
    const derivedConnectionStatus: ConnectionStatus = useGatewayTier
      ? gatewayChat.connectionStatus
      : 'basic';

    // Handle first-login welcome message
    const [hasShownWelcome, setHasShownWelcome] = useState(false);

    useEffect(() => {
      if (firstLoginContext?.isFirstLogin && firstLoginContext.welcomeMessage && !hasShownWelcome && messages.length === 0) {
        // Add the welcome message as if it came from the assistant
        const welcomeMessage: UIMessage = {
          id: `welcome-${Date.now()}`,
          role: 'assistant',
          content: firstLoginContext.welcomeMessage,
          timestamp: Math.floor(Date.now() / 1000),
        };

        setMessages([welcomeMessage]);
        setHasShownWelcome(true);

        // Mark onboarding as complete after showing welcome
        if (firstLoginContext.onboardingId) {
          agentService.completeOnboarding(firstLoginContext.onboardingId).catch(console.error);
        }
      }
    }, [firstLoginContext, hasShownWelcome, messages.length, setMessages]);

    // Persist conversations to localStorage whenever they change
    useEffect(() => {
      saveConversations(conversations);
    }, [conversations]);

    // Transform messages for the UI (preserve source for fallback indicators)
    const uiMessages = useMemo(() => {
      return messages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp * 1000),
        source: msg.source,
      }));
    }, [messages]);

    // Transform conversations for the UI
    const uiConversations = useMemo(() => {
      return conversations.map(conv => ({
        id: conv.id,
        name: conv.name,
        updatedAt: new Date(conv.updated_at * 1000),
      }));
    }, [conversations]);

    const handleSendMessage = useCallback((content: string) => {
      sendMessage(content).then(() => {
        // Auto-save conversation after first response
        if (!activeConversationId && messages.length === 0) {
          const newConv: Conversation = {
            id: `conv-${Date.now()}`,
            name: content.substring(0, 40) + (content.length > 40 ? '...' : ''),
            messages: [],
            created_at: Math.floor(Date.now() / 1000),
            updated_at: Math.floor(Date.now() / 1000),
          };
          setConversations(prev => [newConv, ...prev]);
          setActiveConversationId(newConv.id);
        }
      });
    }, [sendMessage, activeConversationId, messages.length]);

    const handleSelectConversation = useCallback((id: string) => {
      // Save current messages to the active conversation
      if (activeConversationId) {
        setConversations(prev => prev.map(c =>
          c.id === activeConversationId ? { ...c, messages, updated_at: Math.floor(Date.now() / 1000) } : c
        ));
      }
      // Load selected conversation
      const conv = conversations.find(c => c.id === id);
      if (conv) {
        setMessages(conv.messages);
        setActiveConversationId(id);
      }
    }, [activeConversationId, conversations, messages, setMessages]);

    const handleNewChat = useCallback(() => {
      // Save current messages if there are any
      if (activeConversationId && messages.length > 0) {
        setConversations(prev => prev.map(c =>
          c.id === activeConversationId ? { ...c, messages, updated_at: Math.floor(Date.now() / 1000) } : c
        ));
      }
      setActiveConversationId(undefined);
      clearMessages();
    }, [activeConversationId, messages, clearMessages]);

    const handleDeleteConversation = useCallback((id: string) => {
      setConversations(prev => prev.filter(c => c.id !== id));
      if (id === activeConversationId) {
        setActiveConversationId(undefined);
        clearMessages();
      }
    }, [activeConversationId, clearMessages]);

    const handleRenameConversation = useCallback((id: string, newName: string) => {
      setConversations(prev => prev.map(c =>
        c.id === id ? { ...c, name: newName } : c
      ));
    }, []);

    const handleConfirmAction = useCallback((confirmed: boolean, confirmationCode?: string) => {
      if (confirmAction) {
        confirmAction(confirmed, confirmationCode);
      }
    }, [confirmAction]);

    const handleToggleCommandCenter = useCallback(() => {
      setCommandCenterMode(prev => {
        const next = !prev;
        clearMessages();
        return next;
      });
    }, [clearMessages]);

    // Navigate to the canonical in-app Command Center bootstrap route.
    const handleExpandToFullPage = useCallback(() => {
      const returnUrl = window.location.pathname + window.location.search;
      const url = new URL(primePath, window.location.origin);
      url.searchParams.set('openEquanaut', 'true');
      url.searchParams.set('commandCenter', 'true');
      url.searchParams.set('session', sessionKey || 'agent:main:main');
      if (currentOrganization?.id) {
        url.searchParams.set('orgId', currentOrganization.id);
      }
      url.searchParams.set('returnUrl', returnUrl);
      window.location.href = url.toString();
    }, [sessionKey, currentOrganization?.id]);

    /** One-time import: merge localStorage conversations into current list (Spec 054 Phase 4.6). */
    const handleImportFromDevice = useCallback(() => {
      const stored = loadConversations();
      if (stored.length === 0) return;
      const existingIds = new Set(conversations.map(c => c.id));
      const newConvs = stored.filter(c => !existingIds.has(c.id));
      if (newConvs.length === 0) return;
      setConversations(prev => [...newConvs, ...prev]);
    }, [conversations]);

    const handleDismissError = useCallback(() => {
      // Clear error - this is handled by the chat hooks
    }, []);

    return (
      <EquanautSidebar
        isOpen={isOpen}
        onClose={onClose}
        messages={uiMessages}
        conversations={uiConversations}
        activeConversationId={activeConversationId}
        connectionStatus={derivedConnectionStatus}
        isLoading={isLoading}
        error={error}
        executingSteps={executingSteps}
        pendingAction={pendingAction}
        commandCenterMode={commandCenterMode}
        onToggleCommandCenter={handleToggleCommandCenter}
        onExpandToFullPage={handleExpandToFullPage}
        initialCommandCenterTab={initialCommandCenterTab}
        organizationId={currentOrganization?.id}
        sessionKey={sessionKey}
        onSendMessage={handleSendMessage}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        onImportFromDevice={handleImportFromDevice}
        onRenameConversation={handleRenameConversation}
        onDeleteConversation={handleDeleteConversation}
        onDismissError={handleDismissError}
        onConfirmAction={useGatewayTier ? handleConfirmAction : undefined}
      />
    );
  };

// Outer component with FAB toggle and context provider
const EquanautToggleComponent: React.FC<StateProps> = ({ user, currentOrganization }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [firstLoginContext, setFirstLoginContext] = useState<FirstLoginResponse | null>(null);
  const [hasCheckedFirstLogin, setHasCheckedFirstLogin] = useState(false);

  // Check for first-login context when user is authenticated
  useEffect(() => {
    if (!user || hasCheckedFirstLogin) return;

    // Check URL params for first login flag
    const urlParams = new URLSearchParams(window.location.search);
    const isFirstLogin = urlParams.get('firstLogin') === 'true';
    const shouldOpenEquanaut = urlParams.get('openEquanaut') === 'true';

    // Auto-open sidebar when returning from Command Center (Phase 3)
    if (shouldOpenEquanaut && !isFirstLogin) {
      setIsOpen(true);
      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('openEquanaut');
      url.searchParams.delete('session');
      window.history.replaceState({}, '', url.toString());
      setHasCheckedFirstLogin(true);
      return;
    }

    // Check if we've already handled first login
    const handledKey = `${FIRST_LOGIN_KEY}_${user.id}`;
    const hasHandled = localStorage.getItem(handledKey);

    if ((isFirstLogin || shouldOpenEquanaut) && !hasHandled) {
      // Fetch first login context from API
      agentService.getFirstLoginContext()
        .then((response) => {
          if (response.isFirstLogin) {
            setFirstLoginContext(response);
            setIsOpen(true);
            // Mark as handled
            localStorage.setItem(handledKey, 'true');
          }
        })
        .catch((err) => {
          console.error('Failed to get first login context:', err);
        })
        .finally(() => {
          setHasCheckedFirstLogin(true);
          // Clean up URL params
          if (isFirstLogin || shouldOpenEquanaut) {
            const url = new URL(window.location.href);
            url.searchParams.delete('firstLogin');
            url.searchParams.delete('openEquanaut');
            window.history.replaceState({}, '', url.toString());
          }
        });
    } else {
      setHasCheckedFirstLogin(true);
    }
  }, [user, hasCheckedFirstLogin]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Header button style - fits in the top navigation bar
  const headerButtonStyle: React.CSSProperties = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: isOpen ? '#33AA40' : 'transparent',
    color: isOpen ? '#ffffff' : '#9CA6AD',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    marginRight: '8px',
  };

  const headerButtonHoverHandlers = {
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isOpen) {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(51,170,64,0.2)';
        (e.currentTarget as HTMLElement).style.color = '#33AA40';
      }
    },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isOpen) {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
        (e.currentTarget as HTMLElement).style.color = '#9CA6AD';
      }
    },
  };

  return (
    <>
      <button
        style={headerButtonStyle}
        onClick={handleToggle}
        title={isOpen ? 'Close Equanaut' : 'Open Equanaut'}
        {...headerButtonHoverHandlers}
      >
        <BotIcon size={20} color={isOpen ? '#ffffff' : 'currentColor'} />
      </button>
      <AgentContextProvider initialOrganizationId={currentOrganization?.id}>
        <EquanautInner
          user={user}
          currentOrganization={currentOrganization}
          isOpen={isOpen}
          onClose={handleClose}
          firstLoginContext={firstLoginContext}
        />
      </AgentContextProvider>
    </>
  );
};

const mapStateToProps: StateTransform<StateProps> = s => ({
  user: s.user,
  currentOrganization: s.organization ? s.organization.currentOrganization : undefined,
});

export const EquanautToggle = connect(mapStateToProps)(EquanautToggleComponent);

export { default as EquanautSidebar } from './components/EquanautSidebar';
export { default as ChatContainer } from './components/ChatContainer';
export { default as ChatInput } from './components/ChatInput';
export { default as ChatMessage } from './components/ChatMessage';
export { default as ConversationList } from './components/ConversationList';
export { default as ConversationItem } from './components/ConversationItem';
export { default as Header } from './components/Header';
export { default as StatusBar } from './components/StatusBar';
export { default as MessageFeedback } from './components/MessageFeedback';
export { default as MarkdownRenderer } from './components/MarkdownRenderer';
export { default as ToolExecutionPanel } from './components/ToolExecutionPanel';
export { default as ConfirmationDialog } from './components/ConfirmationDialog';
export { default as CommandCenterPanel } from './components/CommandCenterPanel';
export { AgentContextProvider, useAgentContext } from './context/AgentContextProvider';
export { useAgentChat } from './hooks/useAgentChat';
export { useGatewayChat } from './hooks/useGatewayChat';
export { useGatewayConnection } from './hooks/useGatewayConnection';

export default EquanautToggle;
